import React from 'react';
import { useTranslation } from 'react-i18next';
import { InformationCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';

// L'AJUDA DEL LLENÇ DE BUCLES CAUSALS.
//
// Era un bloc de text amb trenta-tres cadenes en anglès escrites entre les etiquetes, i per això
// no es podia traduir sense desmuntar-lo. Ara el contingut és una estructura i el marcatge una
// sola volta: afegir-hi una secció és afegir-hi una entrada.
//
// El text s'ha comprovat contra el codi del llenç abans de traduir-lo: tot el que promet existeix
// —la detecció de bucles, les partícules, el control de velocitat, la mida del node segons el
// valor i el zoom amb Ctrl—. L'única cosa que hi deia i que ja no és certa era que el color del
// node anava «de vermell a verd»: ara és una rampa d'un sol to, i el text ho diu.

const Ratlla = ({ etiqueta, cos }) => (
  <div className="flex items-start gap-2">
    <span className="font-medium text-gray-900 w-32 flex-shrink-0">{etiqueta}</span>
    <span className="text-gray-700">{cos}</span>
  </div>
);

const Seccio = ({ titol, children }) => (
  <section className="mb-6">
    <h4 className="text-sm font-semibold text-gray-900 mb-3 uppercase tracking-wider">{titol}</h4>
    <div className="space-y-2 text-sm">{children}</div>
  </section>
);

const HelpModal = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  if (!isOpen) return null;

  const ratlles = (parells) => parells.map(([e, c]) => (
    <Ratlla key={e} etiqueta={t(`help.${e}`)} cos={t(`help.${c}`)} />
  ));

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true">
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="fixed inset-0 bg-gray-900 bg-opacity-50" onClick={onClose} />

        <div className="relative bg-white rounded-md shadow-xl max-w-2xl w-full">
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <div className="flex items-center gap-2">
              <InformationCircleIcon className="w-6 h-6 text-primary-700" />
              <h3 className="text-lg font-semibold text-gray-900">{t('help.title')}</h3>
            </div>
            <button onClick={onClose} aria-label={t('help.close')}
              className="text-gray-500 hover:text-gray-700 focus:outline-none">
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          <div className="px-6 py-5 max-h-[70vh] overflow-y-auto">
            <Seccio titol={t('help.mouse')}>
              {ratlles([['select', 'selectBody'], ['pan', 'panBody'], ['addVar', 'addVarBody'],
                        ['addLink', 'addLinkBody'], ['del', 'delBody']])}
            </Seccio>

            <Seccio titol={t('help.values')}>
              <p className="text-gray-700">{t('help.valuesBody')}</p>
              <p className="text-gray-500">{t('help.valuesExample')}</p>
            </Seccio>

            <Seccio titol={t('help.links')}>
              {ratlles([['polarity', 'polarityBody'], ['strength', 'strengthBody'],
                        ['delay', 'delayBody']])}
            </Seccio>

            <Seccio titol={t('help.loops')}>
              <p className="text-gray-700">{t('help.loopsBody')}</p>
              {ratlles([['reinforcing', 'reinforcingBody'], ['balancing', 'balancingBody']])}
              <p className="text-gray-500">{t('help.loopsClick')}</p>
            </Seccio>

            <Seccio titol={t('help.sim')}>
              <p className="text-gray-700">{t('help.simBody')}</p>
              {ratlles([['simSize', 'simSizeBody'], ['simColour', 'simColourBody'],
                        ['simParticles', 'simParticlesBody'], ['simSpeed', 'simSpeedBody']])}
            </Seccio>

            <Seccio titol={t('help.colour')}>
              <p className="text-gray-700">{t('help.colourBody')}</p>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>{t('help.colour1')}</li>
                <li>{t('help.colour2')}</li>
              </ul>
            </Seccio>

            <Seccio titol={t('help.keys')}>
              {[['Delete', 'keyDelete'], ['Escape', 'keyEscape'], ['Ctrl / ⌘ + scroll', 'keyZoom']]
                .map(([tecla, clau]) => (
                  <div key={tecla} className="flex items-center gap-3">
                    <kbd className="px-2 py-1 bg-gray-100 border border-gray-200 rounded-md
                                    font-mono text-xs text-gray-700">{tecla}</kbd>
                    <span className="text-gray-700">{t(`help.${clau}`)}</span>
                  </div>
                ))}
            </Seccio>

            <section>
              <h4 className="text-sm font-semibold text-gray-900 mb-3 uppercase tracking-wider">
                {t('help.tips')}
              </h4>
              <ul className="bg-gray-50 border border-gray-200 rounded-md p-4 text-sm text-gray-700
                             list-disc list-inside space-y-1">
                {['tip1', 'tip2', 'tip3', 'tip4'].map((k) => <li key={k}>{t(`help.${k}`)}</li>)}
              </ul>
            </section>
          </div>

          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end">
            <button onClick={onClose}
              className="px-4 py-2 text-sm font-semibold text-white bg-primary-700 rounded-md
                         hover:bg-primary-800 focus:outline-none focus:ring-2 focus:ring-offset-2">
              {t('help.close')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HelpModal;
